import type { PayloadAction } from '@reduxjs/toolkit';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { getPrivateChatButtonsList, parseChatMessage } from '../../../utils/chat';
import { fetchLiveSupportMessagesAction } from '../../apiActions/fetchLiveSupportMessagesAction';
import { fetchPrivateChatOptionsAction } from '../../apiActions/fetchPrivateChatAction';
import { fetchTableLevelMessagesAction } from '../../apiActions/fetchTableLevelMessagesAction';
import { headerActions } from '../header/slice';
import { HeaderModal } from '../header/types';

import type { ChatState, Message, ChatSubscriptionState } from './types';
import { ChatBlockType, ChatType } from './types';

const MAX_MESSAGE_COUNT = 100;

export const gameChatMessagesAdapter = createEntityAdapter<Message, Message['messageId']>({
  selectId: (message) => message.messageId,
  sortComparer: (a, b) => b.timestamp - a.timestamp,
});

export const liveSupportChatMessagesAdapter = createEntityAdapter<Message, Message['messageId']>({
  selectId: (message) => message.messageId,
  sortComparer: (a, b) => b.timestamp - a.timestamp,
});

const initialState: ChatState = {
  chatBlockType: ChatBlockType.LowBalance,
  chatSubscriptionState: 'UNSUBSCRIBED',
  gameChatMessages: gameChatMessagesAdapter.getInitialState(),
  isBetChatAllowed: true,
  isGameChatBlocked: false,
  isGameChatHistoryFetched: false,
  isLiveSupportChatBlocked: false,
  isLiveSupportChatRead: true,
  lastChatMessage: null,
  liveSupportChatMessages: liveSupportChatMessagesAdapter.getInitialState(),
  miniChatMessages: [],
  privateChatOptions: [],
  unreadPrivateMessageCount: 0,
  openedChatType: null,
  isPrivateChatOptionsExpanded: true,
};

const resetUnreadPrivateMessageCount = (state: ChatState): void => {
  if (state.openedChatType === ChatType.GameChat) {
    state.unreadPrivateMessageCount = 0;
  }
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    storeGameChatMessage: (state, action: PayloadAction<Message>) => {
      gameChatMessagesAdapter.addOne(state.gameChatMessages, action.payload);

      const allMessages = gameChatMessagesAdapter.getSelectors().selectAll(state.gameChatMessages);
      if (allMessages.length > MAX_MESSAGE_COUNT) {
        const oldestMessageId = allMessages[allMessages.length - 1]?.messageId;
        // Remove oldest message
        if (oldestMessageId) {
          gameChatMessagesAdapter.removeOne(state.gameChatMessages, oldestMessageId);
        }
      }
    },
    storeLiveSupportMessage: (state, action: PayloadAction<Message>) => {
      liveSupportChatMessagesAdapter.addOne(state.liveSupportChatMessages, action.payload);
    },
    clearLiveSupportMessages: (state) => {
      liveSupportChatMessagesAdapter.removeAll(state.liveSupportChatMessages);
    },
    storeLastChatMessage: (state, action: PayloadAction<Message>) => {
      state.lastChatMessage = action.payload;
    },
    updateGameChatMessage: (state, action: PayloadAction<Message>) => {
      gameChatMessagesAdapter.updateOne(state.gameChatMessages, {
        id: action.payload.messageId,
        changes: { showMessage: action.payload.showMessage },
      });
    },
    setLiveSupportReadStatus: (state, action: PayloadAction<boolean>) => {
      state.isLiveSupportChatRead = action.payload;
    },
    setChatSubscriptionState: (state, action: PayloadAction<ChatSubscriptionState>) => {
      state.chatSubscriptionState = action.payload;
    },
    setChatBlockType: (state, action: PayloadAction<ChatBlockType>) => {
      state.chatBlockType = action.payload;
    },
    setIsBetChatAllowed: (state, action: PayloadAction<boolean>) => {
      state.isBetChatAllowed = action.payload;
    },
    setIsGameChatBlocked: (state, action: PayloadAction<boolean>) => {
      state.isGameChatBlocked = action.payload;
    },
    setIsLiveSupportChatBlocked: (state, action: PayloadAction<boolean>) => {
      state.isLiveSupportChatBlocked = action.payload;
    },
    setUnreadPrivateMessageCounter: (state, action: PayloadAction<number>) => {
      state.unreadPrivateMessageCount = action.payload;
    },
    setOpenedChatType: (state, action: PayloadAction<ChatType | null>) => {
      state.openedChatType = action.payload;
      resetUnreadPrivateMessageCount(state);
    },
    setIsPrivateOptionsExpanded: (state, action: PayloadAction<boolean>) => {
      state.isPrivateChatOptionsExpanded = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLiveSupportMessagesAction.fulfilled, (state, action) => {
        liveSupportChatMessagesAdapter.addMany(state.liveSupportChatMessages, action.payload.map(parseChatMessage));
      })
      .addCase(fetchTableLevelMessagesAction.fulfilled, (state, action) => {
        /*
         * Right now backend is sending duplicate messageId for the private actions
         * We needed to filter it to maintain entity adaptor uniqueness sice addMany doesn't handle duplicate values
         * backend should never send duplicate messages ideally but be should also do check it before adding
         * or After redux version update we can remove this filter with usage of upsertMany
         */
        const foundMessageIds: Record<string, boolean> = {};
        const uniqueMsgList = action.payload
          .filter((msg) => {
            if (foundMessageIds[msg.messageId]) {
              return false;
            }
            foundMessageIds[msg.messageId] = true;
            return true;
          })
          .map(parseChatMessage);
        gameChatMessagesAdapter.addMany(state.gameChatMessages, uniqueMsgList);
      })
      .addCase(fetchTableLevelMessagesAction.pending, (state) => {
        state.isGameChatHistoryFetched = true;
      })
      .addCase(fetchPrivateChatOptionsAction.fulfilled, (state, action) => {
        state.privateChatOptions = getPrivateChatButtonsList(action.payload.privateRequestMap);
      })
      .addCase(headerActions.setOpenedModal, (state, action) => {
        switch (action.payload) {
          case HeaderModal.Chat:
            state.openedChatType = ChatType.GameChat;
            resetUnreadPrivateMessageCount(state);
            break;
          case HeaderModal.Support:
            state.openedChatType = ChatType.SupportChat;
            break;
          default:
            state.openedChatType = null;
            break;
        }
      });
  },
});

export const chatActions = chatSlice.actions;
